import React from "react";
import { motion } from "motion/react";
import { Link } from "react-router-dom";
import { Home, Bed, Waves, Mountain, Trees, Shield, ArrowRight, Phone, Play } from "lucide-react";
import { Button } from "./ui/button";
import { casaPremium, WHATSAPP_LINK } from "../data/mock";

const iconMap = {
  Home: Home,
  Bed: Bed,
  Waves: Waves,
  Mountain: Mountain,
  Trees: Trees,
  Shield: Shield,
};

const CasaPremiumSection = () => {
  const getWhatsAppUrl = () => {
    const message = `Hello! I'm interested in renting the Itanhangá Luxury Villa. I'd like more information.`;
    return `${WHATSAPP_LINK}?text=${encodeURIComponent(message)}`;
  };

  return (
    <section id="casa-premium" className="py-20 lg:py-28 bg-gradient-to-b from-[hsl(var(--sand))] to-white relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[hsl(var(--golden))] rounded-full blur-[150px] opacity-10" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-[hsl(var(--coral))] rounded-full blur-[120px] opacity-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-block text-[hsl(var(--coral))] text-sm font-semibold uppercase tracking-wider mb-4">
            Exclusive Property
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Premium Luxury House
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Experience unparalleled luxury in our exclusive Swiss-designed mansion 
            in the prestigious Itanhangá neighborhood.
          </p>
        </motion.div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Image Gallery */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <img
                  src={casaPremium.images[0]}
                  alt="Itanhangá Luxury Villa - Main View"
                  className="w-full h-64 lg:h-80 object-cover rounded-2xl shadow-xl"
                />
              </div>
              <img
                src={casaPremium.images[1]}
                alt="Itanhangá Luxury Villa - Interior"
                className="w-full h-40 object-cover rounded-xl shadow-lg"
              />
              <img
                src={casaPremium.images[2]}
                alt="Itanhangá Luxury Villa - Pool"
                className="w-full h-40 object-cover rounded-xl shadow-lg"
              />
            </div>
            {/* Video Play Button Overlay */}
            <a
              href={casaPremium.videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-3 rounded-full shadow-lg hover:bg-white transition-colors"
            >
              <Play className="w-6 h-6 text-[hsl(var(--coral))] fill-current" />
            </a>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="font-display text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
              {casaPremium.nameEn}
            </h3>
            <p className="text-[hsl(var(--coral))] font-medium mb-4">
              {casaPremium.tagline}
            </p>
            <p className="text-gray-600 mb-6 leading-relaxed">
              {casaPremium.description}
            </p>

            {/* Features Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
              {casaPremium.features.map((feature, index) => {
                const Icon = iconMap[feature.icon];
                return (
                  <div
                    key={index}
                    className="flex items-center gap-2 bg-white p-3 rounded-xl shadow-sm"
                  >
                    <div className="w-8 h-8 rounded-full bg-[hsl(var(--golden))/0.15] flex items-center justify-center">
                      <Icon className="w-4 h-4 text-[hsl(var(--golden))]" />
                    </div>
                    <span className="text-sm text-gray-700 font-medium">
                      {feature.textEn}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/casa-premium/itanhanga-luxury-villa">
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button className="w-full sm:w-auto bg-[hsl(var(--coral))] hover:bg-[hsl(var(--warm-orange))] text-white rounded-full px-8 py-4 h-auto font-semibold gap-2">
                    View Full Details
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </motion.div>
              </Link>
              <motion.a
                href={getWhatsAppUrl()}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button className="w-full sm:w-auto bg-[hsl(var(--whatsapp))] hover:bg-[hsl(142_69%_38%)] text-white rounded-full px-8 py-4 h-auto font-semibold gap-2">
                  <Phone className="w-5 h-5" />
                  Book via WhatsApp
                </Button>
              </motion.a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CasaPremiumSection;
